import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { EventHandler } from '../tool/EventHandler';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../tool/EventHandlerResult';
import { ModifierKeys } from '../tool/ModifierKeys';
import { LayerBitMask } from './layers/Layer';

/**
 * {@link View} is an abstract class that offers a visualization of data.
 * Each {@link View} has it's own {@link HTMLCanvasElement canvas}-element on which the View's contents are rendered.
 *
 * @implements Interface {@link EventHandler} as View may define it's own EventHandlers.\
 * {@link ToolManager} will propagate the events all the way down to View if {@link ToolManager.activeAction} and {@link ToolManager.activeTool} do not prevent the propagation.
 */
export abstract class View implements EventHandler /*, CursorSource*/ {
    /**
     * Optional {@link HTMLCanvasElement}, that is used to display the {@link View}'s contents.\
     * This {@link View} will use the whole canvas as its own.
     */
    private readonly _canvas: HTMLCanvasElement;

    private resizeObserver?: ResizeObserver;

    private _canvas2D: HTMLCanvasElement;
    /**
     * The default {@link View} constructor.\
     * {@link View} may be initialized without any content,\
     * so it is possible to personalize the Layout without size or other visual elements such as the DOM.
     * @param canvas optional parameter of type {@link HTMLCanvasElement}.\
     * This canvas is to be used to render the contents of this {@link View}.
     */
    protected constructor(
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement
    ) {
        this._canvas = canvas;
        this._canvas2D = canvas2D;
        this.resizeObserver = new ResizeObserver(this.resizeObserve);
        if (this._canvas) {
            this.resizeObserver.observe(this._canvas);
        }
    }

    private resizeObserve = (): void => {
        if (this.onCanvasResize) {
            this.onCanvasResize();
        }
    };

    /**
     * Method to get reference to the {@link HTMLCanvasElement} this {@link View} is using.
     */
    public get canvas(): HTMLCanvasElement | undefined {
        return this._canvas;
    }
    public get get2DCanvas(): HTMLCanvasElement | null {
        return this._canvas2D;
    }

    /**
     * Optional method to be performed by the {@link View} whenever the {@link View.canvas canvas} is resized.
     */
    protected onCanvasResize?(): void;

    /**
     * Method to convert the {@link ReadonlyVec2 client pixel coordinates} to {@link ReadonlyVec2 view centric coordinates}.\
     * The {@link ReadonlyVec2 client pixel coordinates} of the {@link coordinates}-parameter are in range [0, numberOfPixels].\
     * The {@link ReadonlyVec2 viewRelativeCoordinates} this method produces are in between [-1, 1].
     * @param coordinates {@link ReadonlyVec2 client pixel coordinates} to be converted.
     * @returns The view relative coordinates.
     */
    public clientToViewCoordinates(coordinates: ReadonlyVec2): vec2 {
        if (this._canvas) {
            const rect = this._canvas.getBoundingClientRect();
            const x =
                rect.left < rect.right
                    ? (coordinates[0] - rect.left) / (rect.right - rect.left)
                    : (coordinates[0] - rect.right) / (rect.left - rect.right);
            const y =
                rect.bottom < rect.top
                    ? (coordinates[1] - rect.bottom) / (rect.top - rect.bottom)
                    : (coordinates[1] - rect.top) / (rect.bottom - rect.top);
            //x and y are in range [0, 1], but we want them in range [-1, 1]. Note that y is flipped.
            return vec2.fromValues(2 * x - 1, -2 * y + 1);
        }
        return vec2.create();
    }

    /**
     * Convert client coordinates (in px) into canvas coordinates (in px)
     * @param coordinates
     * @returns
     */
    public clientToCanvas(clientCoordinates: ReadonlyVec2): ReadonlyVec2 {
        if (this._canvas) {
            const rect = this._canvas.getBoundingClientRect();
            const x = clientCoordinates[0] - rect.left;
            const y = clientCoordinates[1] - rect.top;
            return vec2.fromValues(x, y);
        }
        throw new Error('Canvas is undefined');
    }

    /**
     * Method to get the size of this {@link View} in pixels.\
     * This is equal to the pixel size of the underlying {@link HTMLCanvasElement canvas}.
     * @returns The size of this {@link View} in pixels.\
     * If {@link _canvas canvas} is undefined, returns vec2(0, 0).
     */
    public get size(): vec2 {
        return this._canvas
            ? vec2.fromValues(
                  this._canvas.clientWidth,
                  this._canvas.clientHeight
              )
            : vec2.fromValues(0, 0);
    }

    /**
     * Optional method to request {@link View} to prepare itself for content update.
     * @Returns True if the {@link View} is prepared for content update. False otherwise.
     */
    public prepareContentUpdate?(): boolean;

    /**
     * Optional method to inform {@link View} that it's contents have been updated.
     * @param contentUpdated Boolean flag indicating whether there were any content changes or not.
     */
    public finishContentUpdate?(contentUpdated: boolean): void;

    public abstract updateView(layers: LayerBitMask): void;

    public abstract updateViewContent(layers: LayerBitMask): boolean;

    /**
     * Optional method to delete the contents of this {@link View}.\
     * View's can define this method if they need to perform some clean-up when removed.\
     * {@link ViewManager} will call this method when the View is to be deleted.
     */
    public delete(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * {@link View Views} may define a default implementation for {@link EventHandler.onMousePress onMousePress}-event.\
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link View}.
     * @param _button The button that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The {@link View} on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link View} reacted to that event and how to proceed.
     */
    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        _canvasCoordinates: vec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * {@link View Views} may define a default implementation for {@link EventHandler.onMouseRelease onMouseRelease}-event.\
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link View}.
     * @param _button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The {@link View} on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link View} reacted to that event and how to proceed.
     */
    public onMouseRelease(
        _button: number,
        _canvasCoordinates: vec2,
        _view?: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * {@link View Views} may define a default implementation for {@link EventHandler.onMouseMove onMouseMove}-event.\
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link View}.
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The {@link View} on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link View} reacted to that event and how to proceed.
     */
    public onMouseMove(
        _canvasCoordinates: vec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * {@link View Views} may define a default implementation for {@link EventHandler.onMouseWheel onMouseWheel}-event.\
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link View}.
     * @param _delta The direction of the MouseWheel event.
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The {@link View} on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link View} reacted to that event and how to proceed.
     */
    public onMouseWheel(
        _delta: -1 | 1,
        _modifierKeys: ModifierKeys,
        _canvasCoordinates: vec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * {@link View Views} may define a default implementation for {@link EventHandler.onKeyPress onKeyPress}-event.\
     * KeyPress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link View}.
     * @param _key The key that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link View} reacted to that event and how to proceed.
     */
    public onKeyPress(
        _key: string,
        _modifierKeys: ModifierKeys,
        _view?: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
